import * as React from 'react';
import { styled } from '@mui/material/styles';
import PropTypes from 'prop-types';
import { Button, CardContent, CircularProgress } from '@mui/material';
import {
    Form,
    required,
    useNotify,
    useSafeSetState,
} from 'ra-core';
import { TextInput } from 'react-admin';
import { useNavigate, Link } from 'react-router-dom';
import { addSentryBreadcrumb, captureRequestFailure } from '../monitoring/sentry';


export const ForgotPasswordForm = (props: LoginFormProps) => {
    const { className } = props;
    const [loading, setLoading] = useSafeSetState(false);
    const notify = useNotify();
    const navigate = useNavigate();

    const submit = (values: FormData) => {
        const forgotPasswordUrl = `${process.env.REACT_APP_API_URL}/auth/forgot-password`;
        setLoading(true);
        addSentryBreadcrumb({
            category: 'auth',
            message: 'Forgot password requested',
            data: {
                endpoint: forgotPasswordUrl,
            },
        });
        fetch(forgotPasswordUrl, {
            method: 'POST',
            body: JSON.stringify(values),
            headers: {
                'Content-Type': 'application/json',
            }
        })
            .then((response) => {
                if (!response.ok) {
                    const error = new Error(response.statusText || 'Forgot password failed');
                    error.status = response.status;
                    throw error;
                }
                return response

            }).then(response => {
                setLoading(false);
                addSentryBreadcrumb({
                    category: 'auth',
                    message: 'Forgot password email sent',
                    data: {
                        endpoint: forgotPasswordUrl,
                        status: response.status,
                    },
                });
                notify('Password reset link has been sent to your email', { type: 'success' });
                setTimeout(()=>{navigate("/")},5000);

            })
            .catch(error => {
                setLoading(false);
                if (!error.status || error.status >= 500) {
                    captureRequestFailure({
                        error,
                        operation: 'forgot_password',
                        url: forgotPasswordUrl,
                        method: 'POST',
                    });
                } else {
                    addSentryBreadcrumb({
                        category: 'auth',
                        message: 'Forgot password rejected',
                        level: 'warning',
                        data: {
                            endpoint: forgotPasswordUrl,
                            status: error.status,
                        },
                    });
                }
                notify(
                    typeof error === 'string'
                        ? error
                        : typeof error === 'undefined' || !error.message
                            ? 'ra.auth.sign_in_error'
                            : error.status >= 500
                                ? 'Password reset is temporarily unavailable. Please try again later.'
                                : error.message,
                    {
                        type: 'warning',
                        messageArgs: {
                            _:
                                typeof error === 'string'
                                    ? error
                                    : error?.status >= 500
                                        ? 'Password reset is temporarily unavailable. Please try again later.'
                                        : error && error.message
                                            ? error.message
                                        : undefined,
                        },
                    }
                );
            });
    };

    return (
        <StyledForm
            onSubmit={submit}
            mode="onChange"
            noValidate
            className={className}
        >
            <CardContent className={LoginFormClasses.content}>
                <TextInput
                    autoFocus
                    source="email"
                    //label={translate('ra.auth.email')}
                    label='Email'
                    validate={required()}
                    fullWidth
                />

                <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={loading}
                    fullWidth
                    className={LoginFormClasses.button}
                >
                    {loading ? (
                        <CircularProgress
                            className={LoginFormClasses.icon}
                            size={19}
                            thickness={3}
                        />
                    ) : (
                        //translate('ra.auth.sign_in')
                        'Send'
                    )}
                </Button>

                <Link to="/login">Login</Link>
            </CardContent>
        </StyledForm>
    );
};

const PREFIX = 'RaLoginForm';

export const LoginFormClasses = {
    content: `${PREFIX}-content`,
    button: `${PREFIX}-button`,
    icon: `${PREFIX}-icon`,
};

const StyledForm = styled(Form, {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
    [`& .${LoginFormClasses.content}`]: {
        width: 300,
    },
    [`& .${LoginFormClasses.button}`]: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    [`& .${LoginFormClasses.icon}`]: {
        margin: theme.spacing(0.3),
    },
}));

export interface LoginFormProps {
    redirectTo?: string;
    className?: string;
}

interface FormData {
    username: string;
    password: string;
}
ForgotPasswordForm.propTypes = {
    redirectTo: PropTypes.string,
};
